import { useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import {
  DORMANT_GRACE_DAYS,
  EXPIRING_DAYS,
  type AnalyticsMemberRow,
  type AnalyticsRange,
  type AnalyticsSummary,
} from '@shared/analytics';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { isAnalyticsExcluded, setAnalyticsExcluded } from '@/lib/analytics';
import { useAnalyticsData } from './useAnalyticsData';

const RANGES: Array<[AnalyticsRange, string, string]> = [
  ['today', '今天', '昨天同时段'],
  ['7d', '近7天', '前7天'],
  ['30d', '近30天', '前30天'],
];
const cardClass =
  'min-w-0 rounded-2xl border border-border bg-card shadow-[0_1px_2px_rgba(80,50,20,0.04)]';
const headClass = 'bg-muted/70 text-xs font-medium text-muted-foreground';
const number = (value: number) => Number(value || 0).toLocaleString('zh-CN');

function Delta({ current, previous }: { current: number; previous: number }) {
  if (current === previous) return <span>持平</span>;
  const pct = previous
    ? Math.round((Math.abs(current - previous) / previous) * 100)
    : 100;
  return current > previous ? (
    <span className="font-semibold text-emerald-700">↑ {pct}%</span>
  ) : (
    <span className="font-semibold text-red-700">↓ {pct}%</span>
  );
}

function Stat({
  label,
  value,
  unit,
  foot,
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  foot: ReactNode;
}) {
  return (
    <div className={cardClass + ' p-4 sm:px-5 sm:py-[18px]'}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1.5 text-2xl font-bold leading-tight tabular-nums text-foreground sm:text-[30px]">
        {value}
        {unit && (
          <span className="ml-1 text-[15px] font-medium text-muted-foreground">
            {unit}
          </span>
        )}
      </p>
      <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
        {foot}
      </p>
    </div>
  );
}

function Compare({
  data,
  field,
  previousLabel,
}: {
  data: AnalyticsSummary;
  field: keyof AnalyticsSummary['current'];
  previousLabel: string;
}) {
  return (
    <>
      <span>
        {previousLabel} {number(data.previous[field])}
      </span>
      <Delta current={data.current[field]} previous={data.previous[field]} />
    </>
  );
}

function Bar({ value, max }: { value: number; max: number }) {
  return (
    <span
      aria-hidden
      className="hidden h-2 w-28 justify-end overflow-hidden rounded-full bg-muted sm:flex"
    >
      <span
        className="h-2 rounded-full bg-primary"
        style={{ width: `${(value / Math.max(1, max)) * 100}%` }}
      />
    </span>
  );
}

function Overview({
  data,
  previousLabel,
}: {
  data: AnalyticsSummary;
  previousLabel: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? data.daily : data.daily.slice(0, 7);
  const max = Math.max(...data.daily.map((r) => r.claims));
  const rate = data.members.valid
    ? Math.round((data.members.activeValid / data.members.valid) * 100)
    : 0;
  const grid =
    'grid grid-cols-[52px_repeat(3,minmax(0,1fr))] items-center gap-2 px-3 sm:grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)] sm:px-5';
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3">
        {(
          [
            ['访客', 'visitors', ''],
            ['看过主题详情', 'detailVisitors', ''],
            ['点过客服', 'contactVisitors', ''],
            ['领取的会员', 'claimMembers', '人'],
            ['领取的资源', 'claims', '个'],
          ] as const
        ).map(([label, field, unit]) => (
          <Stat
            key={field}
            label={label}
            value={number(data.current[field])}
            unit={unit}
            foot={
              <Compare
                data={data}
                field={field}
                previousLabel={previousLabel}
              />
            }
          />
        ))}
        <Stat
          label="活跃率"
          value={rate}
          unit="%"
          foot={`${number(data.members.activeValid)} / ${number(data.members.valid)} 位有效会员本期领取过`}
        />
      </div>

      <div className={cardClass + ' overflow-hidden'}>
        <div className="flex items-center justify-between px-4 py-4 sm:px-5">
          <h3 className="text-base font-semibold text-foreground">每日情况</h3>
          <span className="text-xs text-muted-foreground">
            近 {expanded ? 30 : 7} 天 · 从新到旧
          </span>
        </div>
        <div className={grid + ' h-10 ' + headClass}>
          <span>日期</span>
          <span className="text-right">访客</span>
          <span className="text-right">领取会员</span>
          <span className="text-right">领取资源</span>
        </div>
        {rows.map((r, i) => (
          <div
            key={r.day}
            className={
              grid +
              ' min-h-11 border-t border-border/60 text-sm tabular-nums sm:text-[15px]'
            }
          >
            <span>
              {r.day.slice(5)}
              {i === 0 && (
                <span className="ml-1 text-xs text-muted-foreground">今天</span>
              )}
            </span>
            <span className="text-right">{number(r.visitors)}</span>
            <span className="text-right">{number(r.claimMembers)}</span>
            <span className="flex items-center justify-end gap-2.5">
              <Bar value={r.claims} max={max} />
              <span className="min-w-6 text-right">{number(r.claims)}</span>
            </span>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="min-h-12 w-full border-t border-border/60 text-sm font-medium text-cta hover:bg-accent/60 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cta"
        >
          {expanded ? '收起，只看近 7 天' : '展开近 30 天'}
        </button>
      </div>
    </div>
  );
}

type ListKey = 'top' | 'expiring' | 'dormant';

function daysLeft(expiry: string, today: string) {
  return Math.round(
    (Date.parse(expiry + 'T00:00:00Z') - Date.parse(today + 'T00:00:00Z')) /
      86400000,
  );
}

function Members({
  data,
  previousLabel,
  onOpenMember,
}: {
  data: AnalyticsSummary;
  previousLabel: string;
  onOpenMember?: (passcode: string) => void;
}) {
  const [list, setList] = useState<ListKey>('top');
  const lists: Array<[ListKey, string, AnalyticsMemberRow[], string]> = [
    ['top', '领取最多', data.topMembers, '本期领取资源最多的前 10 位'],
    [
      'expiring',
      '快到期',
      data.expiringMembers,
      `${EXPIRING_DAYS} 天内到期的有效会员，到期早的在前`,
    ],
    [
      'dormant',
      '沉睡',
      data.dormantMembers,
      `开通满 ${DORMANT_GRACE_DAYS} 天、近 30 天没领取过，快到期的在前`,
    ],
  ];
  const [, , rows, desc] = lists.find(([key]) => key === list)!;
  const grid =
    'grid grid-cols-[minmax(0,1.25fr)_minmax(0,0.7fr)_minmax(0,0.95fr)_minmax(0,1.15fr)_14px] items-center gap-1.5 px-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_20px] sm:gap-2 sm:px-5';
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
        <Stat
          label="有效会员"
          value={number(data.members.valid)}
          foot="未冻结、未过期、已激活"
        />
        <Stat
          label="新开会员"
          value={number(data.current.newMembers)}
          foot={
            <Compare
              data={data}
              field="newMembers"
              previousLabel={previousLabel}
            />
          }
        />
        <Stat
          label="快到期"
          value={number(data.members.expiring)}
          foot={`${EXPIRING_DAYS} 天内到期`}
        />
        <Stat
          label="沉睡会员"
          value={number(data.members.dormant)}
          foot={`开通满 ${DORMANT_GRACE_DAYS} 天、30 天没领`}
        />
      </div>

      <div className={cardClass + ' overflow-hidden'}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-5">
          <div
            role="group"
            aria-label="会员名单"
            className="flex flex-wrap gap-1.5 sm:gap-2"
          >
            {lists.map(([key, label, items]) => {
              const on = key === list;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setList(key)}
                  className={
                    'flex min-h-10 items-center gap-1.5 rounded-full border px-3 text-sm sm:px-3.5 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cta ' +
                    (on
                      ? 'border-cta bg-cta font-semibold text-cta-foreground'
                      : 'border-muted bg-muted text-accent-foreground hover:bg-accent')
                  }
                >
                  {label}
                  <span
                    className={
                      'rounded-full px-1.5 text-xs leading-[18px] tabular-nums ' +
                      (on ? 'bg-white/25' : 'bg-border')
                    }
                  >
                    {items.length}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[13px] text-muted-foreground">
            {desc}
            {onOpenMember && rows.length > 0 && ' · 点击查看详情'}
          </p>
        </div>
        <div className={grid + ' h-10 ' + headClass}>
          <span>兑换码</span>
          <span className="text-right">本期领取</span>
          <span className="text-right">最近领取</span>
          <span className="text-right">到期日</span>
          <span />
        </div>
        {rows.length === 0 ? (
          <p className="border-t border-border/60 py-10 text-center text-sm text-muted-foreground">
            {list === 'top' ? '本期还没有会员领取' : '暂无会员'}
          </p>
        ) : (
          <div className="max-h-[520px] overflow-y-auto">
            {rows.map((m) => {
              const left = daysLeft(m.expiryDate, data.to);
              const soon = left <= EXPIRING_DAYS;
              return (
                <button
                  key={m.passcode}
                  type="button"
                  disabled={!onOpenMember}
                  onClick={() => onOpenMember?.(m.passcode)}
                  aria-label={`查看会员 ${m.passcode} 详情`}
                  className={
                    grid +
                    ' min-h-[52px] w-full border-t border-border/60 text-left text-[13px] text-foreground hover:bg-accent/50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cta disabled:cursor-default disabled:hover:bg-transparent sm:text-sm'
                  }
                >
                  <span className="truncate font-mono font-semibold tracking-wide">
                    {m.passcode}
                  </span>
                  <span className="text-right tabular-nums">
                    {number(m.claims)}
                  </span>
                  <span className="text-right tabular-nums text-accent-foreground">
                    {m.lastClaimDay ? m.lastClaimDay.slice(5) : '从未领取'}
                  </span>
                  <span className="flex flex-col items-end leading-tight tabular-nums">
                    <span>{m.expiryDate}</span>
                    {soon && (
                      <span className="text-xs font-semibold text-red-700">
                        {left === 0 ? '今天到期' : `${left} 天后`}
                      </span>
                    )}
                  </span>
                  <ChevronRight
                    aria-hidden
                    className="h-3.5 w-3.5 text-muted-foreground"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Topics({ data }: { data: AnalyticsSummary }) {
  const max = Math.max(0, ...data.topTopics.map((t) => t.viewers));
  const grid =
    'grid grid-cols-[28px_minmax(0,1fr)_64px_56px] items-center gap-2 px-3 sm:grid-cols-[40px_minmax(0,2.4fr)_minmax(0,1.6fr)_minmax(0,1fr)] sm:px-5';
  return (
    <div className={cardClass + ' overflow-hidden'}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-5">
        <h3 className="text-base font-semibold text-foreground">
          热门主题 Top 10
        </h3>
        <span className="text-xs text-muted-foreground">
          按本期看过详情的人数排序
        </span>
      </div>
      <div className={grid + ' h-10 ' + headClass}>
        <span>排名</span>
        <span>主题</span>
        <span className="text-right">看过的人</span>
        <span className="text-right">领取会员</span>
      </div>
      {data.topTopics.length === 0 ? (
        <p className="border-t border-border/60 py-10 text-center text-sm text-muted-foreground">
          本期暂无主题访问
        </p>
      ) : (
        data.topTopics.map((t, i) => (
          <div
            key={t.name}
            className={
              grid +
              ' min-h-12 border-t border-border/60 text-[13px] tabular-nums sm:text-sm'
            }
          >
            <span
              className={
                i < 3
                  ? 'flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-900'
                  : 'w-6 text-center text-muted-foreground'
              }
            >
              {i + 1}
            </span>
            <span className="truncate">{t.name}</span>
            <span className="flex items-center justify-end gap-2.5">
              <Bar value={t.viewers} max={max} />
              <span className="min-w-7 text-right">{number(t.viewers)}</span>
            </span>
            <span className="text-right">{number(t.claims)}</span>
          </div>
        ))
      )}
    </div>
  );
}

export function AnalyticsPanel({
  onOpenRedemptions,
  onOpenMemberDetail,
}: {
  onOpenRedemptions?: () => void;
  onOpenMemberDetail?: (passcode: string) => void;
} = {}) {
  const { range, data, error, busy, setRange, refresh } = useAnalyticsData();
  const [excluded, setExcluded] = useState(isAnalyticsExcluded);
  const [tab, setTab] = useState('overview');
  const previousLabel = RANGES.find(([r]) => r === range)![2];
  const failed = data
    ? data.failures.passcode + data.failures.quota + data.failures.other
    : 0;
  return (
    <section aria-label="运营数据" className="min-w-0 space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold leading-tight text-foreground">
            运营数据
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            每个数字都和上一期的同一时刻对比
          </p>
          <label className="mt-2 inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-full border border-border bg-card px-3 text-[13px] text-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 accent-cta"
              checked={excluded}
              onChange={(e) => {
                setAnalyticsExcluded(e.target.checked);
                setExcluded(e.target.checked);
              }}
            />
            本浏览器的访问不计入统计
            <span className="text-muted-foreground">· 登录后台会自动勾选</span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="时间范围"
            className="flex flex-1 gap-0.5 rounded-xl bg-muted p-1"
          >
            {RANGES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={range === value}
                onClick={() => setRange(value)}
                className={
                  'min-h-9 flex-1 whitespace-nowrap rounded-[9px] px-3.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-cta ' +
                  (range === value
                    ? 'bg-card font-semibold text-cta shadow-sm'
                    : 'text-accent-foreground hover:text-foreground')
                }
              >
                {label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11 rounded-xl"
            aria-label="刷新"
            onClick={refresh}
            disabled={busy}
          >
            <RefreshCw aria-hidden className={busy ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {failed > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900"
        >
          <AlertTriangle aria-hidden className="mt-1 h-4 w-4 shrink-0" />
          <p>
            {range === 'today' ? '今天' : '本期'}有 {number(failed)}{' '}
            次领取失败：兑换码无效/冻结/过期/作废{' '}
            {number(data!.failures.passcode)} 次，额度不足{' '}
            {number(data!.failures.quota)} 次，其他{' '}
            {number(data!.failures.other)} 次。
            {onOpenRedemptions && (
              <button
                type="button"
                onClick={onOpenRedemptions}
                className="ml-2 whitespace-nowrap font-semibold text-orange-800 underline-offset-2 hover:underline"
              >
                去看领取记录 ›
              </button>
            )}
          </p>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="min-w-0 gap-5">
        <TabsList
          aria-label="运营数据分类"
          className="h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0"
        >
          {[
            ['overview', '概览'],
            ['members', '会员'],
            ['topics', '热门主题'],
          ].map(([value, label]) => (
            <TabsTrigger
              key={value}
              value={value}
              className="-mb-px min-h-11 flex-none rounded-none border-0 border-b-2 border-transparent px-4 text-[15px] text-muted-foreground shadow-none data-[state=active]:border-cta data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-cta data-[state=active]:shadow-none"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {error ? (
          <div role="alert" className={cardClass + ' space-y-3 p-5'}>
            <p className="text-sm text-red-700">{error}</p>
            <Button variant="outline" className="min-h-11" onClick={refresh}>
              重试
            </Button>
          </div>
        ) : data ? (
          <>
            <TabsContent value="overview" className="min-w-0">
              <Overview data={data} previousLabel={previousLabel} />
            </TabsContent>
            <TabsContent value="members" className="min-w-0">
              <Members
                data={data}
                previousLabel={previousLabel}
                onOpenMember={onOpenMemberDetail}
              />
            </TabsContent>
            <TabsContent value="topics" className="min-w-0">
              <Topics data={data} />
            </TabsContent>
          </>
        ) : (
          <p
            role="status"
            className={
              cardClass + ' py-12 text-center text-sm text-muted-foreground'
            }
          >
            正在读取数据…
          </p>
        )}
      </Tabs>
    </section>
  );
}
